import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { useVenueLinesQuery } from "../hooks/routes";
import { useCurrentClimbType, useCurrentVenueUuid } from "../hooks/venues";
import { BoulderMaps, SportMaps } from "./Map/MapsList";
import { useRoutesUiStore } from "./RoutesProvider";
import { useDisplayedMapsWithDefault, useRoutesUiContext } from "./RoutesProvider/RoutesProvider";
import { useRoutesTableStore } from "./RoutesTable/RoutesProvider2";

export default function Maps() {
  const { routes, routesPending, filteredRouteUuids, canChangeSelection } = useRoutesUiContext();

  const {
    selectedRouteUuids,
    addToSelection,
    removeFromSelection,
    setSelectedRouteUuids,
    setHighlightedRouteUuids,
  } = useRoutesTableStore(
    useShallow((state) => ({
      selectedRouteUuids: state.selectedRouteUuids,
      addToSelection: state.addToSelection,
      removeFromSelection: state.removeFromSelection,
      setSelectedRouteUuids: state.setSelectedRouteUuids,
      setHighlightedRouteUuids: state.setHighlightedRouteUuids,
    })),
  );
  const { setContextMenu, mapMode, createRouteData } = useRoutesUiStore(
    useShallow((state) => ({
      setContextMenu: state.setContextMenu,
      mapMode: state.mapMode,
      createRouteData: state.createRouteData,
    })),
  );

  const currentVenueUuid = useCurrentVenueUuid();
  const { data: lines } = useVenueLinesQuery(currentVenueUuid);
  const climbType = useCurrentClimbType();
  const { maps, isPending: mapsPending } = useDisplayedMapsWithDefault();

  const onSelectSingle = useCallback(
    (uuid: string) => setSelectedRouteUuids([uuid]),
    [setSelectedRouteUuids],
  );
  const onClearSelection = useCallback(() => setSelectedRouteUuids([]), []);

  const routesWithCreating = useMemo(() => {
    if (createRouteData && routes) {
      return [...routes, { ...createRouteData, uuid: "CREATING" }];
    }
    return routes;
  }, [routes, createRouteData]);

  const selectedUuids = useMemo(() => {
    if (createRouteData) {
      return new Set(["CREATING"]);
    }
    return selectedRouteUuids;
  }, [selectedRouteUuids, createRouteData]);

  const filteredUuids = useMemo(() => {
    if (createRouteData) {
      return new Set([...filteredRouteUuids, "CREATING"]);
    }
    return filteredRouteUuids;
  }, [filteredRouteUuids, createRouteData]);

  if (climbType === "BOULDER") {
    return (
      <BoulderMaps
        routes={routesWithCreating}
        routesPending={routesPending}
        filteredRouteUuids={filteredUuids}
        selectedRouteUuids={selectedUuids}
        onSelectSingleRoute={onSelectSingle}
        onSetSelectedRouteUuids={setSelectedRouteUuids}
        onSelectRoutes={addToSelection}
        onDeselectRoutes={removeFromSelection}
        onClearSelection={onClearSelection}
        canChangeSelection={createRouteData ? false : canChangeSelection}
        onRouteContextMenu={(uuid: string, x: number, y: number) =>
          setContextMenu({ x, y, routeUuid: uuid, type: "route" })
        }
        onMapContextMenu={(
          mapUuid: string,
          mapPosition: { x: number; y: number },
          x: number,
          y: number,
        ) => setContextMenu({ x, y, mapUuid, mapPosition, type: "map" })}
        onBrushStart={() => setContextMenu(null)}
        maps={maps}
        mapsPending={mapsPending}
        brushEnabled={mapMode === "select"}
      />
    );
  } else if (climbType === "SPORT") {
    return (
      <SportMaps
        onSelectLine={(_, uuids) => setHighlightedRouteUuids(uuids)}
        routes={routesWithCreating}
        lines={lines}
        filteredRouteUuids={filteredUuids}
        selectedRouteUuids={selectedUuids}
        onSelectSingleRoute={onSelectSingle}
        onSetSelectedRouteUuids={setSelectedRouteUuids}
        onSelectRoutes={addToSelection}
        onDeselectRoutes={removeFromSelection}
        onClearSelection={onClearSelection}
        canChangeSelection={createRouteData ? false : canChangeSelection}
        onRouteContextMenu={(uuid: string, x: number, y: number) =>
          setContextMenu({ x, y, routeUuid: uuid, type: "route" })
        }
        onLineContextMenu={(uuid: string, x: number, y: number) =>
          setContextMenu({
            x,
            y,
            lineUuid: uuid,
            type: "line",
          })
        }
        maps={maps}
        brushEnabled={mapMode === "select"}
      />
    );
  }
}
